import React from 'react';
import { Briefcase, Tag, Euro, Star, Calendar, Factory, Newspaper } from 'lucide-react';
import { PartnerNewsletterSwitch } from './PartnerNewsletterSwitch';
import { PartnerTagsRow } from './PartnerTagsRow';
import type { PartnerExtras } from '../../pages/partners/usePartnerEntity';
import { STATUS_CONFIG, type PartnerStatusKey } from './partnerStatusConfig';

// Stessa mappa etichette di PartnerKanbanCard.tsx (nessun export condiviso lì, duplicazione
// minima accettata per non toccare quel file in questo task).
const PARTNER_TYPE_LABELS: Record<string, string> = {
  service_provider: 'Fornitore di Servizi',
  consultant: 'Consulente',
  supplier: 'Fornitore',
  agency: 'Agenzia',
  freelancer: 'Freelancer',
};

interface PartnerInfoExtrasProps {
  extras: PartnerExtras;
}

// Notion-style property row — stile IDENTICO a `PropertyRow` di LeadDesktopLayout.tsx
// (righe 80-109), non esportato da lì: duplicato qui volutamente per non introdurre un
// import cross-cartella di un componente privato.
const PropertyRow = ({
  icon: Icon,
  label,
  children
}: {
  icon: any;
  label: string;
  children: React.ReactNode;
}) => (
  <div className="flex items-center justify-between gap-2 py-2 px-2 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/[0.03] rounded transition-colors">
    {/* Entrambi i lati dimensionati dal contenuto (come in origine): a colonna stretta
        il testo va a capo invece di essere tagliato. L'unica aggiunta è il `gap-2` sulla
        riga, senza il quale etichetta e valore si toccavano ("Commissione0%"). */}
    <div className="flex items-center space-x-2.5">
      <Icon className="h-4 w-4 text-[#787774] dark:text-gray-500 flex-shrink-0" />
      <span className="text-sm text-[#787774] dark:text-gray-400">{label}</span>
    </div>
    {children}
  </div>
);

const formatCurrency = (amount?: number) => {
  if (amount === undefined || amount === null) return '';
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount);
};

const RatingStars = ({ rating }: { rating: number }) => (
  <div className="flex items-center gap-1">
    {[1, 2, 3, 4, 5].map((star) => (
      <Star
        key={star}
        className={`h-3.5 w-3.5 ${star <= rating ? 'text-yellow-400 fill-current' : 'text-gray-300 dark:text-gray-600'}`}
      />
    ))}
    <span className="text-xs text-[#787774] dark:text-gray-500 ml-1">({rating}/5)</span>
  </div>
);

/**
 * Property rows partner-specifiche (Task 3, piano "Partner drawer unificato"): montate
 * nel ramo `entityType === 'partner'` di LeadDesktopLayout/LeadMobileLayout dopo le
 * property rows agnostiche (email, telefono, indirizzo, P.IVA, ecc. — già coperte dal
 * view-model `mapPartnerToClientView`). Stile identico alle righe lead, vedi
 * `PropertyRow` sopra. Dati da `usePartnerEntity().partnerExtras`.
 */
export function PartnerInfoExtras({ extras }: PartnerInfoExtrasProps) {
  const statusConfig = extras.status
    ? STATUS_CONFIG[extras.status as PartnerStatusKey] || STATUS_CONFIG.inactive
    : null;
  const typeLabel = extras.partner_type ? PARTNER_TYPE_LABELS[extras.partner_type] || extras.partner_type : null;

  const hasAnyExtra =
    typeLabel || statusConfig || extras.commission_rate != null || extras.hourly_rate != null ||
    extras.specialization || extras.business_sector || extras.performance_rating != null ||
    extras.collaboration_start_date || extras.newsletter_subscribed !== undefined;

  // La riga Tag (profilo segnalatore) c'e' anche senza altri campi extra
  if (!hasAnyExtra && !extras.id) return null;

  return (
    <div className="space-y-0.5">
      {typeLabel && (
        <PropertyRow icon={Briefcase} label="Tipo Partner">
          <span className="text-sm text-[#37352F] dark:text-white truncate max-w-[220px]">{typeLabel}</span>
        </PropertyRow>
      )}

      {statusConfig && (
        <PropertyRow icon={Tag} label="Stato">
          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusConfig.bgClass}`}>
            {statusConfig.label}
          </span>
        </PropertyRow>
      )}

      {/* La provvigione si vede e si cambia solo nella sezione Provvigioni (profilo segnalatore):
          external_partners.commission_rate non e' piu' la fonte e poteva mostrare un valore vecchio */}

      {extras.hourly_rate != null && (
        <PropertyRow icon={Euro} label="Tariffa Oraria">
          <span className="text-sm text-[#37352F] dark:text-white">{formatCurrency(extras.hourly_rate)}</span>
        </PropertyRow>
      )}

      {extras.specialization && (
        <PropertyRow icon={Briefcase} label="Specializzazione">
          <span className="text-sm text-[#37352F] dark:text-white truncate max-w-[220px]">{extras.specialization}</span>
        </PropertyRow>
      )}

      {extras.business_sector && (
        <PropertyRow icon={Factory} label="Settore">
          <span className="text-sm text-[#37352F] dark:text-white truncate max-w-[220px]">{extras.business_sector}</span>
        </PropertyRow>
      )}

      {extras.performance_rating != null && (
        <PropertyRow icon={Star} label="Valutazione">
          <RatingStars rating={extras.performance_rating} />
        </PropertyRow>
      )}

      {extras.collaboration_start_date && (
        <PropertyRow icon={Calendar} label="Inizio Collaborazione">
          <span className="text-sm text-[#37352F] dark:text-white">
            {new Date(extras.collaboration_start_date).toLocaleDateString('it-IT')}
          </span>
        </PropertyRow>
      )}

      {extras.id && <PartnerTagsRow partnerId={extras.id} />}

      {/* Assente finche' la migration 20261016_partner_consenso_newsletter non crea la colonna */}
      {extras.id && extras.newsletter_subscribed !== undefined && (
        <PropertyRow icon={Newspaper} label="Newsletter">
          <PartnerNewsletterSwitch partnerId={extras.id} subscribed={extras.newsletter_subscribed} />
        </PropertyRow>
      )}
    </div>
  );
}

export default PartnerInfoExtras;
